import { get } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Container, Row, Col, Tile, Icon, CaptionText, blue600, green600, BodyText, Alert, LoadingSpinner } from '@paypalcorp/pp-react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';

import { isAcceptableType, inputAccept } from '../utils/fileValidation';
import { UploadDocument } from './UploadCardAction';
import { DOCUMENT_FILTER_ERROR_CODES } from '../constants/status';
import { trackTransition } from '../../../../framework/store/FPTI/FPTIActions';
import { safeStringify } from '../../../../shared-utilities/safeStringify';

import './UploadCard.less';

const MAX_FILE_SIZE_IN_MB = 10;
const MIN_FILE_SIZE_IN_KB = 30;
const BYTES_PER_KB = 1024;
const BYTES_PER_MB = 1024 * 1024;
const LOCALE_PREFIX = 'US/pages/AppPendPage/DocumentsRequired';
const DOC_UPLOAD_FILTERS = 'US/pages/AppPendPage/DocFilterError';

export class UploadCard extends Component {
    static propTypes = {
        captionText: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        verificationId: PropTypes.string.isRequired,
        targetEntity: PropTypes.object.isRequired,
        uploadedDocuments: PropTypes.object.isRequired,
        uploadDocument: PropTypes.func.isRequired,
        acceptableDocuments: PropTypes.array,
        onDocumentUpload: PropTypes.func.isRequired,
        isAlreadyUploaded: PropTypes.bool.isRequired,
        isMobile: PropTypes.bool.isRequired,
        intl: intlShape.isRequired,
        trackError: PropTypes.func.isRequired
    };

    state = {
        currentFileInputMetadata: '',
        currentFileInputUrl: '',
        currentFileInputError: '',
        showLoadingScreen: false,
        disableDocumentUpload: false
    };

    fileInputRef = React.createRef();

    setError = (error, formData) => {
        this.setState({
            currentFileInputError: error,
            currentFileInputMetadata: '',
            currentFileInputUrl: ''
        });
        if (error) {
            this.props.trackError(error, formData);
        }
    };

    setDisableUpload = (bool) => {
        this.setState({
            disableDocumentUpload: bool
        });
    };

    setLoadingScreen = (bool) => {
        this.setState({
            showLoadingScreen: bool
        });
    };

    unsetUploadFile = () => {
        this.fileInputRef.current.value = null;
    };

    setUploadServerError = (formData, statusCode) => {
        this.setDisableUpload(false);
        this.setLoadingScreen(false);
        this.unsetUploadFile();
        if (statusCode && DOCUMENT_FILTER_ERROR_CODES.indexOf(statusCode) != -1) {
            return this.setError(`${DOC_UPLOAD_FILTERS}.${statusCode}`, formData);
        } else {
            return this.setError(`${LOCALE_PREFIX}.ERROR-FILE-UPLOAD`, formData);
        }
    };

    onFileInputChange = async(e) => {
        const { currentFileInputUrl } = this.state;
        const { intl, type, uploadDocument, verificationId, targetEntity } = this.props;
        const currentFile = e.target.files[0];
        const currentFileDetails = { name: get(currentFile, 'name'), type: get(currentFile, 'type'), size: get(currentFile, 'size') };
        capeLog && capeLog(`appPend file details: ${safeStringify(currentFileDetails)}`);
        const { entityType, id, precedence, referenceType } = targetEntity;

        const formData = new FormData();
        formData.append('document', currentFile);
        formData.append('verificationId', verificationId);
        formData.append('type', type);
        formData.append('entityType', entityType);
        formData.append('id', id);
        formData.append('precedence', precedence);
        formData.append('referenceType', referenceType);

        if (!isAcceptableType(currentFile.type)) {
            capeLog && capeLog(`appPend invalid file type: ${currentFile.type}`);

            this.setError(`${LOCALE_PREFIX}.ERROR-FILE-TYPE`, formData);
            this.setDisableUpload(false);
            this.setLoadingScreen(false);
        } else if (this.isFileSizeUnderLimit(currentFile.size)) {
            capeLog && capeLog(`appPend file size under limit: ${currentFile.size}`);

            const fileSizeError = intl.formatMessage({ id: `${DOC_UPLOAD_FILTERS}.443` }, { minFileSize: MIN_FILE_SIZE_IN_KB });
            this.setError(fileSizeError, formData);
            this.setDisableUpload(false);
            this.setLoadingScreen(false); 
        } else if (this.isFileSizeOverLimit(currentFile.size)) {
            capeLog && capeLog(`appPend file size over limit: ${currentFile.size}`);

            const fileSizeError = intl.formatMessage({ id: `${LOCALE_PREFIX}.ERROR-FILE-SIZE` }, { maxFileSize: MAX_FILE_SIZE_IN_MB });
            this.setError(fileSizeError, formData);
            this.setDisableUpload(false);
            this.setLoadingScreen(false);
        } else {
            this.setError('', formData);
            this.setDisableUpload(true);
            this.setLoadingScreen(true);

            try {
                const docUploadResponse = await this.props.onDocumentUpload(formData);
                if (!docUploadResponse.success) {
                    return this.setUploadServerError(formData, get(docUploadResponse, 'error.issue', ''));
                }

                const currentFileInputMetadata = {
                    name: currentFile.name,
                    size: this.convertBytesToMb(currentFile.size)
                };
                this.setState({
                    currentFileInputError: '',
                    currentFileInputUrl: this.getDataUrl(currentFile),
                    currentFileInputMetadata,
                    showLoadingScreen:false
                }, () => {
                    uploadDocument(currentFileInputUrl, currentFileInputMetadata, type);
                });

            } catch (e) {
                return this.setUploadServerError(formData);
            }
        }
    };

    isFileSizeUnderLimit = (size) => {
        return size < (MIN_FILE_SIZE_IN_KB * BYTES_PER_KB);
    };

    isFileSizeOverLimit = (size) => {
        return size > (MAX_FILE_SIZE_IN_MB * BYTES_PER_MB);
    };

    convertBytesToMb = (bytes) => {
        const converted = bytes / BYTES_PER_MB;
        return `${converted.toFixed(2)} MB`;
    };

    getDataUrl = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                resolve(reader.result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    acceptableDocuments = () => {
        const { acceptableDocuments } = this.props;
        return acceptableDocuments.map(item => <li className="documentList" key={item}>{item}</li>);
    };

    iconCheckCircle = () => (<Icon className = "check-circle-alt" name="check-circle-alt" style= {{ color: green600 }} />);
    iconUploadIcon = () => (<Icon className = "uploadIcon" name={this.props.isMobile ? 'camera-add' : 'upload'} style= {{ color: blue600 }} size="lg" />);
    uploadSpinner = () => (<LoadingSpinner className="uploadSpinner" screenReaderText="loading" size="md" />);
    uploadText = () => (
        <CaptionText className="captionText">
            <FormattedMessage id={`${LOCALE_PREFIX}.LOADING-UPLOAD-FILE`} />
        </CaptionText>
    );

    renderIcon = () => {
        const { isAlreadyUploaded, uploadedDocuments, type } = this.props;
        return (isAlreadyUploaded || uploadedDocuments[type]) ? this.iconCheckCircle() : this.iconUploadIcon();
    };

    renderDocumentType = () => {
        const { uploadedDocuments, type, isAlreadyUploaded } = this.props;
        if (isAlreadyUploaded) {
            return null;
        }
        return (uploadedDocuments[type]
            ? (<CaptionText className="uploadCardFileInfo" style={{ color: '#6C7378' }}>{uploadedDocuments[type].name}</CaptionText>)
            : <ul className="documentUl" >{this.acceptableDocuments()}</ul>
        );
    };

    showAlert = (alertMessage) => (
        <Alert className="alert" type="error">
            <FormattedMessage id={alertMessage} />
        </Alert>
    );

    onUpload = (event) => {
        if (event.charCode === 13 || event.charCode === 32) {
            this.fileInputRef.current.click();
        }
    };

    render() {
        const { disableDocumentUpload, currentFileInputError, showLoadingScreen } = this.state;
        const { isAlreadyUploaded, captionText } = this.props;
        const disabledCursor = (isAlreadyUploaded || disableDocumentUpload) ? 'default' : 'pointer';
        return (
            <div className="UploadCard">
                <label className="galleryInputLabel">
                    <input type="file" className="galleryInput"
                        ref={this.fileInputRef}
                        onChange={this.onFileInputChange}
                        accept={inputAccept()}
                        disabled={isAlreadyUploaded || disableDocumentUpload}
                    />
                    <BodyText className="inputText">
                        <Tile card cardRadius="lg" className="uploadCard" style={{ cursor: disabledCursor }} tabIndex={isAlreadyUploaded || disableDocumentUpload ? '-1' : '0'} onKeyPress={this.onUpload}>
                            <Tile.Content>
                                <Container className="uploadCardGridContainer">
                                    <Row className="uploadCardRow">
                                        <Col xs={2} className="leftIcon" >
                                            {showLoadingScreen ? this.uploadSpinner() : this.renderIcon()}
                                        </Col>
                                        <Col xs={10} >
                                            {showLoadingScreen ? this.uploadText()
                                                : <>
                                                    <CaptionText className="captionText">{captionText}</CaptionText>
                                                    {this.renderDocumentType()}
                                                </>
                                            }
                                        </Col>
                                    </Row>
                                    {currentFileInputError && this.showAlert(currentFileInputError)}
                                </Container>
                            </Tile.Content>
                        </Tile>
                    </BodyText>
                </label>
            </div>
        );
    }
}

export const mapStateToProps = state => ({
    isMobile: get(state, 'serverData.isMobile', false)
});

export const mapDispatchToProps = dispatch => ({
    onDocumentUpload(formData) {
        dispatch(trackTransition('apply_pp_credit_uploadDocument', 'prepare_pp_credit_uploadDocument', {
            ...(formData.has('type') && { document_type: formData.get('type') })
        }));
        return dispatch(UploadDocument(formData));
    },
    trackError(error, formData) {
        dispatch(trackTransition('apply_pp_credit_uploadDocument', 'error_pp_credit_uploadDocument', {
            ...(formData.has('type') && { document_type: formData.get('type') }),
            ...(error && { error_type: error })
        }));
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(UploadCard));
